import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const appsList = [
    { name: "Quiz & Learn", path: "/quiz-learn-info" },
    { name: "NCLEXSmart Prep", path: "/nclexsmart-prep" },
    { name: "PMHNP Smart Prep", path: "/pmhnp-smart-prep" },
    { name: "CDIP Smart Prep", path: "/cdip-smart-prep" },
    { name: "Kingdom Kids Daily Devotional", path: "/kingdom-kids-daily-devotional" },
    { name: "RiseUp 365", path: "/riseup-365" },
    { name: "Daily Grace 365", path: "/daily-grace-365" }
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/90 border-b border-slate-800 shadow-2xl transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        
        {/* Brand Logo Image Placeholder */}
        <Link to="/" className="flex items-center space-x-3 group">
          <img 
            src="/Logo.png" 
            alt="AssuranceTech Innovation Logo" 
            className="h-20 w-auto object-contain transition group-hover:scale-105"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="h-10 w-10 bg-blue-600 rounded-xl hidden items-center justify-center text-white font-black text-xl shadow-lg">
            A
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link to="/" className={`text-sm font-semibold transition hover:text-blue-400 ${location.pathname === '/' ? 'text-blue-400' : 'text-slate-300'}`}>
            Home
          </Link>

          <div className="relative" onMouseLeave={() => setDropdownOpen(false)}>
            <button 
              onMouseEnter={() => setDropdownOpen(true)}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="text-sm font-semibold text-slate-300 hover:text-blue-400 flex items-center gap-1 focus:outline-none py-2"
            >
              Apps ▼
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 w-80 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl py-3 mt-1 animate-in fade-in">
                <div className="px-4 py-2 border-b border-slate-800 mb-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Our Digital Ecosystem</p>
                </div>
                {appsList.map((app, idx) => (
                  <Link 
                    key={idx} 
                    to={app.path}
                    onClick={() => setDropdownOpen(false)}
                    className="block px-4 py-2.5 text-sm font-medium text-slate-300 hover:bg-blue-600/20 hover:text-blue-400 transition"
                  >
                    {app.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/about" className={`text-sm font-semibold transition hover:text-blue-400 ${location.pathname === '/about' ? 'text-blue-400' : 'text-slate-300'}`}>
            About Us
          </Link>
          <Link to="/contact" className={`text-sm font-semibold transition hover:text-blue-400 ${location.pathname === '/contact' ? 'text-blue-400' : 'text-slate-300'}`}>
            Contact
          </Link>
        </nav>

        {/* Desktop Action CTA */}
        <div className="hidden md:block">
          <Link 
            to="/quiz-learn" 
            className="bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm px-5 py-2.5 rounded-xl shadow-lg shadow-blue-500/25 transition"
          >
            Launch Web App
          </Link>
        </div>

        {/* Mobile Hamburger Button */}
        <div className="flex md:hidden items-center">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-slate-300 hover:text-white p-2 focus:outline-none"
            aria-label="Toggle Mobile Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

      </div>

      {/* Mobile Menu Dropdown Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-slate-900 border-b border-slate-800 px-4 pt-4 pb-6 space-y-4 animate-in slide-in-from-top-2">
          <Link 
            to="/" 
            onClick={() => setMobileMenuOpen(false)}
            className="block text-sm font-semibold text-slate-300 hover:text-blue-400 py-1"
          >
            Home
          </Link>

          <div className="space-y-2 pt-2 border-t border-slate-800">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Apps Ecosystem</p>
            {appsList.map((app, idx) => (
              <Link 
                key={idx} 
                to={app.path}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-sm font-medium text-slate-400 hover:text-blue-400 py-1 pl-2"
              >
                • {app.name}
              </Link>
            ))}
          </div>

          <div className="space-y-2 pt-2 border-t border-slate-800">
            <Link 
              to="/about" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-semibold text-slate-300 hover:text-blue-400 py-1"
            >
              About Us
            </Link>
            <Link 
              to="/contact" 
              onClick={() => setMobileMenuOpen(false)}
              className="block text-sm font-semibold text-slate-300 hover:text-blue-400 py-1"
            >
              Contact
            </Link>
          </div>

          <div className="pt-2">
            <Link 
              to="/quiz-learn" 
              onClick={() => setMobileMenuOpen(false)}
              className="w-full text-center block bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm py-3 rounded-xl shadow-md transition"
            >
              Launch Web App
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
